import React from 'react';
import { SelectOption } from 'global';
import { Controller } from 'react-hook-form';
import { Control } from 'react-hook-form/dist/types/form';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import { CircularProgress, InputAdornment, TextField, TextFieldProps, Typography } from '@mui/material';

interface Props<
  Option extends SelectOption = SelectOption,
  Multiple extends boolean = false,
  FormValues extends FieldValues = FieldValues,
> extends Omit<AutocompleteProps<Option, Multiple, false, false>, 'renderInput'> {
  isLoading?: boolean;
  control: Control<FormValues>;
  textFieldProps?: Partial<TextFieldProps>;
  name: UseControllerProps<FormValues>['name'];
  extraAction?: (nextValue: any) => void;
}

export const AutocompleteField = <
  Option extends SelectOption = SelectOption,
  Multiple extends boolean = false,
  FormValues extends FieldValues = FieldValues,
>({
  name,
  control,
  options,
  isLoading = false,
  textFieldProps = {},
  extraAction,
  ...other
}: Props<Option, Multiple, FormValues>) => (
  <Controller
    name={name}
    control={control}
    render={({ field: { ref, value, onChange, ...field }, fieldState }) => {
      const isMultiple = other.multiple ?? false;
      const currentValue = value ?? ((isMultiple ? undefined : null) as any);
      return (
        <Autocomplete<Option, Multiple, false, false>
          fullWidth
          ref={ref}
          options={options}
          value={currentValue}
          getOptionLabel={option => option?.label || ''}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          onChange={(_, value) => {
            onChange(value);
            extraAction?.(value);
          }}
          renderOption={(props, item) => (
            <li {...props} key={item.value}>
              <Typography>{item.label}</Typography>
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : ''}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              {...textFieldProps}
            />
          )}
          {...field}
          {...other}
        />
      );
    }}
  />
);
